import departments, { DepartmentsUnion } from "../departments";
import { ProfilePageProps } from "../../components/pages/SearchPage/queries/types/ProfilePageProps";
import {
  GetListing_listing,
  GetListing_listing_customFields_value_CustomCheckboxFieldValue,
} from "../../components/pages/ListingPage/queries/types/GetListing";
import { isEmpty, uniq } from "lodash";
import isSSR from "../isSSR";
import { Hit } from "./mapHitToListing";
import { SearchPageProps } from "../../components/pages/SearchPage/SearchPage";

export const MAX_RECENTLY_VIEWED_LISTINGS = 10;
export const RECENTLY_VIEWED_LISTINGS_LOCAL_STORE_KEY = "ar-recently-viewed-listing-ids";

export const SIZE_FIELDS = {
  size_guide: "Tops & dresses",
  pants_size: "Bottoms",
  shoe_size: "Shoes",
  accessories_size: "Accessories",
};

export const SIZE_FIELDS_REVERSED = Object.fromEntries(
  Object.entries(SIZE_FIELDS).map(([key, value]) => [value, key])
);

export enum LongTailPageType {
  product = "product",
  brand_category = "brand_category",
}

export interface LongTailPage {
  key: string;
  title: string;
  pageType: LongTailPageType;
}

export interface DiscountEligibilityPage {
  isDiscountEligibilityPage: boolean;
  title: string;
  discountPercent: number;
}

export interface ConciergePage {
  airrobeConciergePerson: ProfilePageProps;
}

export type FilterProps = {
  department?: DepartmentsUnion;
  isDesignerBrand?: boolean;
  brand?: string;
  collectionId?: number;
  profilePage?: ProfilePageProps;
  isProfilePage?: boolean;
  latitude?: string;
  longitude?: string;
  longTailPage?: LongTailPage;
  discountEligibilityPage?: DiscountEligibilityPage;
  conciergePage?: ConciergePage;
  isConciergePage?: boolean;
};

export function applyPageFilters(filterProps: FilterProps) {
  const filters = [];

  if (filterProps?.collectionId) {
    filters.push(`collection_ids:${filterProps.collectionId}`);
  }

  if (filterProps?.isProfilePage) {
    filters.push(`author:"${filterProps.profilePage.person.username}"`);
  }

  if (filterProps?.isDesignerBrand) {
    filters.push("is_designer_brand:true");
  }

  if (filterProps?.brand) {
    filters.push(`brand_key:"${filterProps.brand}"`);
  }

  if (filterProps?.department) {
    const searchFilterValue = departments[filterProps.department]?.searchFilterValue;
    if (searchFilterValue) filters.push(`department:"${searchFilterValue}"`);
  }

  if (filterProps?.longTailPage?.key) {
    filters.push(`long_tail_page_key:"${filterProps.longTailPage.key}"`);
  }

  if (filterProps?.discountEligibilityPage?.isDiscountEligibilityPage) {
    filters.push(
      `seller_commission_percent >= ${filterProps.discountEligibilityPage.discountPercent}`
    );
  }

  if (filterProps?.isConciergePage) {
    filters.push("is_concierge:true");
  }

  return filters.join(" AND ");
}

export function getSearchAnalyticsTags(searchPageProps: SearchPageProps) {
  const tags = [];

  if (searchPageProps?.collectionId) {
    tags.push("Collection Page");
  }

  if (searchPageProps?.isProfilePage) {
    tags.push("Profile Page");
  }

  if (searchPageProps?.isDesignerBrand) {
    tags.push("Designer Search Page");
  }

  if (searchPageProps?.brand) {
    tags.push("Brand Search Page");
  }

  if (searchPageProps?.department && departments[searchPageProps.department]) {
    tags.push(`${departments[searchPageProps.department].label} Search Page`);
  }

  if (searchPageProps?.longTailPage?.key) {
    tags.push(`Long Tail Page`);
  }

  if (searchPageProps?.discountEligibilityPage?.isDiscountEligibilityPage) {
    tags.push(`Discounts Eligible Search Page`);
  }

  if (tags.length === 0) {
    tags.push("Unknown Search Page");
  }

  if (isSSR()) {
    tags.push("Server Side", "SSR");
  }

  return tags;
}

const getDepartmentFilter = (listing: GetListing_listing) => {
  const customField = listing.customFields.find((cf) => cf.key === "apparel_category");
  if (!customField) return null;

  const customFieldValue = customField.value as
    | GetListing_listing_customFields_value_CustomCheckboxFieldValue
    | undefined;
  const value = customFieldValue?.selectedOptions.find(Boolean)?.title;
  if (!value) return null;

  return `department:"${value}"`;
};

const getBrandFilter = (listing: GetListing_listing) => {
  return `brand:"${listing.brand}"`;
};

const getAuthorFilter = (listing: GetListing_listing) => {
  return `author:"${listing.author.username}"`;
};

const getCategoryFilter = (listing: GetListing_listing) => {
  const value = listing.category.url;
  if (!value) return null;

  return `category_id:"${value}"`;
};

const getColourFilter = (listing: GetListing_listing) => {
  const customField = listing.customFields.find((cf) => cf.key === "colour");
  if (!customField) return null;
  const customCheckboxFieldValue = customField.value as GetListing_listing_customFields_value_CustomCheckboxFieldValue | null;

  const values = customCheckboxFieldValue?.selectedOptions.map((so) => so.title);
  if (isEmpty(values)) return null;

  return values.map((val) => `color:"${val}"`).join(" OR ");
};

const getSizeFilter = (listing: GetListing_listing) => {
  const customField = listing.customFields.find((cf) => Object.keys(SIZE_FIELDS).includes(cf.key));
  if (!customField) return null;

  const sizeFieldTitle = SIZE_FIELDS[customField.key];
  const customCheckboxFieldValue = customField.value as GetListing_listing_customFields_value_CustomCheckboxFieldValue | null;

  const values = customCheckboxFieldValue?.selectedOptions.map(
    (so) => `${sizeFieldTitle}|${so.title}`
  );
  if (isEmpty(values)) return null;

  return values.map((val) => `size:"${val}"`).join(" OR ");
};

const getOccasionFilter = (listing: GetListing_listing) => {
  const customField = listing.customFields.find((cf) => cf.key === "occasion");
  if (!customField) return null;

  const customCheckboxFieldValue = customField.value as GetListing_listing_customFields_value_CustomCheckboxFieldValue | null;

  const values = customCheckboxFieldValue?.selectedOptions.map((so) => so.title);
  if (isEmpty(values)) return null;

  return values.map((val) => `occasion:"${val}"`).join(" OR ");
};

const getDiscountedFilter = () => {
  return `is_price_reduced:true`;
};

const getNotDiscountedFilter = () => {
  return `NOT is_price_reduced:true`;
};

const excludeListingFilter = (listing: GetListing_listing) => {
  return `NOT id:"${listing.id}"`;
};

export function getMoreFromSameAuthorFilters(listing: GetListing_listing) {
  return [excludeListingFilter(listing), getAuthorFilter(listing)].join(" AND ");
}

export function getDealsFilters(listing: GetListing_listing) {
  return [
    excludeListingFilter(listing),
    getDepartmentFilter(listing),
    getBrandFilter(listing),
    getCategoryFilter(listing),
    getDiscountedFilter(),
  ]
    .filter(Boolean)
    .map((filter) => `(${filter})`)
    .join(" AND ");
}

export function getSimilarFilters(listing: GetListing_listing) {
  return [
    excludeListingFilter(listing),
    getDepartmentFilter(listing),
    getBrandFilter(listing),
    getCategoryFilter(listing),
    getColourFilter(listing),
    getSizeFilter(listing),
    getNotDiscountedFilter(),
  ]
    .filter(Boolean)
    .map((filter) => `(${filter})`)
    .join(" AND ");
}

export function getMoreBrandFilters(listing: GetListing_listing) {
  return [
    excludeListingFilter(listing),
    getDepartmentFilter(listing),
    getBrandFilter(listing),
    getCategoryFilter(listing),
    getNotDiscountedFilter(),
  ]
    .filter(Boolean)
    .map((filter) => `(${filter})`)
    .join(" AND ");
}

export function getMoreSizeFilters(listing: GetListing_listing) {
  return [
    excludeListingFilter(listing),
    getDepartmentFilter(listing),
    getCategoryFilter(listing),
    getSizeFilter(listing),
  ]
    .filter(Boolean)
    .map((filter) => `(${filter})`)
    .join(" AND ");
}

export function getRecentlyViewedListingFilters(listing: GetListing_listing): string | null {
  if (!isSSR()) {
    const recentlyViewListingIds =
      JSON.parse(localStorage.getItem(RECENTLY_VIEWED_LISTINGS_LOCAL_STORE_KEY)) || [];

    return recentlyViewListingIds
      .filter((listingId: number) => listingId !== listing.id)
      .map((listingId: number) => `id:"${listingId}"`)
      .join(" OR ");
  } else {
    return null;
  }
}

export function updateRecentlyViewedListings(currentListingId: number) {
  if (!isSSR()) {
    const oldRecentlyViewedListingIds =
      JSON.parse(localStorage.getItem(RECENTLY_VIEWED_LISTINGS_LOCAL_STORE_KEY)) || [];

    // Puts current listing id at the start of the array, removes duplicates, then limits the array size to MAX_RECENTLY_VIEWED_LISTINGS
    const newRecentlyViewedListingIds = uniq([
      currentListingId,
      ...oldRecentlyViewedListingIds,
    ]).slice(0, MAX_RECENTLY_VIEWED_LISTINGS);

    localStorage.setItem(
      RECENTLY_VIEWED_LISTINGS_LOCAL_STORE_KEY,
      JSON.stringify(newRecentlyViewedListingIds)
    );
  }
}

export function getLongTailPageFilters(items: Hit[]) {
  const firstHit = items[0];
  const excludedIds = items.map((item) => item.objectID);

  const exclusionFilters = excludedIds.map((id) => `NOT objectID:${JSON.stringify(id)}`);
  const brandFilter = firstHit.brand && `brand:"${firstHit.brand}"`;
  const categoryFilter = firstHit.category_id && `category_id:"${firstHit.category_id}"`;
  return [...exclusionFilters, brandFilter, categoryFilter]
    .filter(Boolean)
    .map((filter) => `(${filter})`)
    .join(" AND ");
}
